import React, { useState, useEffect } from 'react';
import DragDropLayout, { triggerHaptic } from './Layouts/DragDropLayout';

const MultiplicationRaceGame = ({ gameData, onComplete }) => {
    const [timeLeft, setTimeLeft] = useState(gameData?.time_limit || 60);
    const [userAnswers, setUserAnswers] = useState({});
    const [score, setScore] = useState(0);
    const [gameStarted, setGameStarted] = useState(false);
    const [gameEnded, setGameEnded] = useState(false);
    const [shuffledRows, setShuffledRows] = useState([]);
    const [shuffledCols, setShuffledCols] = useState([]);
    const [selectedCell, setSelectedCell] = useState(null); // { row, col }

    const grid = gameData?.grid;
    const emptyCells = grid?.cells?.filter(cell => !cell.filled) || [];

    useEffect(() => {
        if (grid) {
            const rowsWithIdx = grid.row_numbers.map((num, idx) => ({ num, idx }));
            const colsWithIdx = grid.col_numbers.map((num, idx) => ({ num, idx }));
            setShuffledRows([...rowsWithIdx].sort(() => Math.random() - 0.5));
            setShuffledCols([...colsWithIdx].sort(() => Math.random() - 0.5));
        }
    }, [grid]);

    useEffect(() => {
        if (!gameStarted || gameEnded) return;
        const timer = setInterval(() => {
            setTimeLeft((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    endGame();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(timer);
    }, [gameStarted, gameEnded]);

    const startGame = () => {
        setGameStarted(true);
        triggerHaptic([100, 50, 100]);
    };

    const endGame = () => {
        if (gameEnded) return;
        setGameEnded(true);
        const correctCount = Object.entries(userAnswers).filter(([key, value]) => {
            const [row, col] = key.split('-').map(Number);
            const cell = grid.cells.find(c => c.row === row && c.col === col);
            return cell && parseInt(value) === cell.answer;
        }).length;

        const finalScore = correctCount * (gameData.points_per_correct || 10);
        setScore(finalScore);
        triggerHaptic([200, 100, 200, 100, 300]);

        setTimeout(() => {
            onComplete?.(finalScore);
        }, 4000);
    };

    const handleKeyPress = (val) => {
        if (!selectedCell || gameEnded) return;
        const cellId = `${selectedCell.row}-${selectedCell.col}`;
        const currentVal = userAnswers[cellId] || '';
        
        triggerHaptic(20);

        if (val === 'C') {
            setUserAnswers(prev => ({ ...prev, [cellId]: '' }));
        } else {
            if (currentVal.length >= 3) return;
            setUserAnswers(prev => ({ ...prev, [cellId]: currentVal + val }));
        }
    };

    if (!gameData || !grid) {
        return (
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center border-blue-100 animate-pulse bg-white">
                <div className="text-6xl md:text-[100px] mb-8">🏎️</div>
                <h3 className="text-base md:text-3xl font-black text-blue-900 tracking-tighter uppercase">Ruošiama trasa...</h3>
            </div>
        );
    }

    if (!gameStarted) {
        return (
            <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
                <div className="premium-glass rounded-2xl md:rounded-[4rem] p-6 md:p-12 lg:p-24 text-center shadow-2xl border-white/20 bg-white/5 backdrop-blur-xl animate-slide-up select-none flex flex-col items-center max-w-4xl w-full relative overflow-hidden">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(59,130,246,0.1)_0%,transparent_70%)] pointer-events-none" />
                    <div className="relative z-10 space-y-6 md:space-y-12 w-full">
                        <div className="text-6xl md:text-[120px] animate-float drop-shadow-[0_20px_50px_rgba(59,130,246,0.4)]">🏁</div>
                        <div className="space-y-4">
                            <h2 className="text-3xl md:text-7xl font-black text-white tracking-tighter uppercase leading-none">
                                Dauginimo <span className="text-blue-400 italic">Greitis</span>
                            </h2>
                            <p className="text-lg md:text-2xl font-bold text-slate-400 italic font-serif leading-relaxed px-6">
                                "Ar esi pasiruošęs tapti greičiausiu skaičiuotoju trasoje? Turi {gameData.time_limit} sekundžių užpildyti visą lentelę!"
                            </p>
                        </div>

                        <div className="flex flex-col md:flex-row justify-center gap-4">
                            <div className="bg-blue-900/40 text-white rounded-2xl md:rounded-[3rem] p-6 md:p-10 flex-1 shadow-2xl border-2 border-blue-400/30 relative overflow-hidden group">
                                <p className="text-[10px] font-black text-blue-400 uppercase tracking-widest mb-2">Ląstelių Kiekis</p>
                                <p className="text-3xl md:text-6xl font-black italic">{emptyCells.length}</p>
                            </div>
                            <div className="bg-rose-900/40 text-white rounded-2xl md:rounded-[3rem] p-6 md:p-10 flex-1 shadow-2xl border-2 border-rose-500/30 relative overflow-hidden group">
                                <p className="text-[10px] font-black text-rose-400 uppercase tracking-widest mb-2">Laiko Limitas</p>
                                <p className="text-3xl md:text-6xl font-black italic">{gameData.time_limit}s</p>
                            </div>
                        </div>

                        <button
                            onClick={startGame}
                            className="premium-button-3d w-full py-6 md:py-10 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-2xl md:rounded-[3rem] font-black text-xl md:text-4xl shadow-2xl uppercase tracking-[0.2em] border-b-8 border-blue-800 active:translate-y-2 active:border-b-0 transition-all"
                        >
                            STARTAS! 🚀
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const hud = (
        <div className="premium-glass rounded-xl md:rounded-[2.5rem] p-3 md:p-6 border-white/20 shadow-2xl relative overflow-hidden bg-slate-950 text-white border-2 border-blue-500/20">
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-[80px] pointer-events-none"></div>
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 relative z-10">
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 md:w-16 md:h-16 bg-blue-600/20 rounded-xl md:rounded-2xl flex items-center justify-center text-xl md:text-3xl border-2 border-blue-400/30 animate-pulse">
                        🏎️
                    </div>
                    <div>
                        <h3 className="text-base md:text-2xl font-black tracking-tighter uppercase">
                            Dauginimo <span className="text-blue-400 italic">Grandinė</span>
                        </h3>
                    </div>
                </div>

                <div className="flex items-center gap-2 md:gap-4 w-full md:w-auto">
                    <div className={`flex-1 md:flex-none bg-slate-900 px-4 py-2 md:px-8 md:py-4 rounded-xl md:rounded-2xl border-2 transition-all duration-500 ${timeLeft <= 10 ? 'border-rose-500 bg-rose-500/10 animate-pulse' : 'border-blue-500/30'}`}>
                        <p className="text-[8px] md:text-[10px] font-black text-blue-400 uppercase tracking-widest leading-none mb-1">Laikas</p>
                        <p className={`text-sm md:text-2xl font-black font-mono leading-none ${timeLeft <= 10 ? 'text-rose-400' : 'text-white'}`}>{timeLeft}s</p>
                    </div>
                    <div className="flex-1 md:flex-none bg-slate-900 px-4 py-2 md:px-8 md:py-4 rounded-xl md:rounded-2xl border-2 border-purple-500/30">
                        <p className="text-[8px] md:text-[10px] font-black text-purple-400 uppercase tracking-widest leading-none mb-1">Taškai</p>
                        <p className="text-sm md:text-2xl font-black text-white leading-none font-mono">⭐ {score}</p>
                    </div>
                </div>
            </div>

            <div className="mt-3 md:mt-4 w-full bg-slate-800 rounded-full h-2 p-0.5 shadow-inner overflow-hidden border border-white/10">
                <div 
                    className={`h-full transition-all duration-1000 rounded-full ${timeLeft <= 10 ? 'bg-rose-500 shadow-[0_0_10px_rgba(244,63,94,0.5)]' : 'bg-gradient-to-r from-blue-400 to-purple-600'}`}
                    style={{ width: `${(timeLeft / gameData.time_limit) * 100}%` }}
                />
            </div>
        </div>
    );

    const choices = (
        <div className="w-full py-4 md:py-8 overflow-x-auto custom-scrollbar">
            <div className="premium-glass rounded-2xl md:rounded-[4rem] p-4 md:p-8 shadow-2xl border-white/20 bg-white/5 backdrop-blur-xl relative overflow-hidden flex flex-col items-center min-w-[500px]">
                <table className="w-full border-separate border-spacing-1 md:border-spacing-3">
                    <thead>
                        <tr>
                            <th className="w-12 h-12 md:w-20 md:h-20 bg-slate-800 rounded-xl md:rounded-2xl flex items-center justify-center text-lg md:text-3xl font-black text-white shadow-xl border-b-4 border-black">×</th>
                            {shuffledCols.map((col, idx) => (
                                <th key={idx} className="w-12 h-12 md:w-20 md:h-20 bg-blue-900/40 rounded-xl md:rounded-2xl text-lg md:text-3xl font-black text-blue-300 border-2 border-blue-500/30">
                                    {col.num}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {shuffledRows.map((row, rowIdx) => (
                            <tr key={rowIdx}>
                                <th className="w-12 h-12 md:w-20 md:h-20 bg-emerald-900/40 rounded-xl md:rounded-2xl text-lg md:text-3xl font-black text-emerald-300 border-2 border-emerald-500/30">
                                    {row.num}
                                </th>
                                {shuffledCols.map((col, colIdx) => {
                                    const cell = grid.cells.find(c => c.row === row.idx && c.col === col.idx);
                                    const cellId = `${row.idx}-${col.idx}`;
                                    const userAnswer = userAnswers[cellId];
                                    const isSelected = selectedCell?.row === row.idx && selectedCell?.col === col.idx;
                                    const isCorrect = gameEnded && parseInt(userAnswer) === cell?.answer;
                                    const isWrong = gameEnded && userAnswer && parseInt(userAnswer) !== cell?.answer;

                                    return (
                                        <td key={colIdx} className="p-0 text-center">
                                            {cell?.filled ? (
                                                <div className="w-12 h-12 md:w-20 md:h-20 bg-slate-800/50 rounded-xl md:rounded-2xl flex items-center justify-center text-lg md:text-2xl font-black text-slate-500 border-2 border-dashed border-slate-700 opacity-50">
                                                    {cell.answer}
                                                </div>
                                            ) : (
                                                <button
                                                    onClick={() => !gameEnded && setSelectedCell({ row: row.idx, col: col.idx })}
                                                    className={`
                                                        w-12 h-12 md:w-20 md:h-20 rounded-xl md:rounded-2xl text-lg md:text-2xl font-black flex items-center justify-center transition-all duration-300 border-b-4
                                                        ${isSelected ? 'bg-indigo-600 text-white border-indigo-800 scale-110 shadow-2xl z-20 relative -translate-y-1' : 
                                                          isCorrect ? 'bg-emerald-500 text-white border-emerald-700 border-b-0' :
                                                          isWrong ? 'bg-rose-500 text-white border-rose-700 border-b-0' :
                                                          'bg-white/10 text-white border-white/10 hover:bg-white/20 active:translate-y-1 active:border-b-0'}
                                                    `}
                                                >
                                                    {userAnswer || ''}
                                                </button>
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );

    const targets = (
        <div className="space-y-4 md:space-y-8 flex flex-col items-center w-full max-w-4xl mx-auto py-4 md:py-8">
            <div className="premium-glass rounded-[2rem] md:rounded-[3.5rem] p-6 md:p-10 shadow-2xl border-white/20 bg-slate-900 relative overflow-hidden flex flex-col items-center w-full">
                <div className="absolute top-0 right-0 p-4 md:p-12 text-6xl md:text-8xl opacity-[0.05] select-none pointer-events-none text-white">📡</div>
                <div className="w-full space-y-6 md:space-y-10 relative z-10 text-center">
                    <div className="space-y-4">
                        <div className="bg-slate-950 rounded-2xl md:rounded-[2.5rem] p-4 md:p-8 border-2 border-slate-800 shadow-inner group">
                            <p className="text-2xl md:text-5xl font-black text-white font-mono leading-none">
                                {selectedCell ? (
                                    <>
                                        <span className="text-emerald-400">{grid.row_numbers[selectedCell.row]}</span>
                                        <span className="mx-2 text-slate-600">×</span>
                                        <span className="text-blue-400">{grid.col_numbers[selectedCell.col]}</span>
                                        <span className="mx-2 text-slate-600">=</span>
                                    </>
                                ) : 'PASIRINKTI LĄSTELĘ'}
                            </p>
                            <div className="mt-4 md:mt-6 h-12 md:h-16 flex items-center justify-center">
                                <span className="text-4xl md:text-6xl font-black text-cyan-400 animate-pulse font-mono drop-shadow-[0_0_20px_rgba(34,211,238,0.5)]">
                                    {selectedCell ? (userAnswers[`${selectedCell.row}-${selectedCell.col}`] || '_') : ''}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-3 gap-2 md:gap-4 max-w-xs md:max-w-sm mx-auto">
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 'C', 0, '✓'].map((btn) => (
                            <button
                                key={btn}
                                onClick={() => btn === '✓' ? endGame() : handleKeyPress(btn)}
                                className={`
                                    h-14 md:h-20 rounded-xl md:rounded-[1.5rem] text-xl md:text-3xl font-black flex items-center justify-center transition-all duration-150 border-b-4 shadow-lg
                                    ${btn === '✓' ? 'bg-emerald-500 text-white border-emerald-700 active:bg-emerald-600 active:translate-y-1 active:border-b-0' : 
                                      btn === 'C' ? 'bg-rose-500 text-white border-rose-700 active:bg-rose-600 active:translate-y-1 active:border-b-0' :
                                      'bg-white text-slate-800 border-slate-200 hover:bg-slate-50 active:translate-y-1 active:border-b-0'}
                                    ${!selectedCell && btn !== '✓' ? 'opacity-20 pointer-events-none' : ''}
                                `}
                            >
                                {btn}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <button
                onClick={endGame}
                className="premium-button-3d w-full py-5 md:py-8 bg-gradient-to-r from-rose-600 to-rose-700 text-white rounded-xl md:rounded-[3rem] font-black text-lg md:text-3xl shadow-2xl border-b-8 border-rose-900 active:translate-y-1 active:border-b-0 transition-all uppercase tracking-widest"
            >
                BAIGTI LENKTYNES 🏁
            </button>
        </div>
    );

    return (
        <div className="min-h-screen bg-slate-950">
            <DragDropLayout 
                hud={hud}
                choices={choices}
                targets={targets}
            />

            {gameEnded && (
                <div className="fixed inset-0 flex items-center justify-center z-[2000] p-4">
                    <div className="absolute inset-0 bg-slate-950/90 backdrop-blur-2xl animate-fade-in" />
                    <div className="max-w-xl w-full p-8 md:p-16 rounded-[3rem] md:rounded-[5rem] bg-white shadow-2xl relative animate-pop-in border-[10px] md:border-[16px] border-blue-600 text-center">
                        <div className="absolute -top-12 left-1/2 -translate-x-1/2 w-24 h-24 md:w-32 md:h-32 bg-white rounded-full flex items-center justify-center text-4xl md:text-6xl shadow-2xl border-4 border-blue-600 animate-bounce">
                            🏆
                        </div>
                        
                        <div className="space-y-6 md:space-y-10 pt-8 md:pt-12">
                            <h2 className="text-4xl md:text-6xl font-black uppercase tracking-tighter text-slate-900 leading-none">
                                FINIŠAS!
                            </h2>
                            <div className="bg-blue-50 p-6 md:p-10 rounded-[2rem] md:rounded-[3rem] border-2 border-blue-100">
                                <p className="text-[10px] font-black text-blue-500 uppercase tracking-widest mb-4">Rezultatas</p>
                                <div className="flex items-center justify-center gap-2 mb-6">
                                    <span className="text-5xl md:text-8xl font-black text-slate-900 leading-none">{score}</span>
                                    <span className="text-sm md:text-xl font-black text-blue-400 uppercase tracking-widest mt-6 md:mt-10">TAŠKAI</span>
                                </div>
                                <p className="text-sm md:text-xl font-bold text-slate-600 italic font-serif leading-relaxed">
                                    "Puikus greitis! Tavo skaičiavimo įgūdžiai tiesiog neįtikėtini."
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MultiplicationRaceGame;
